import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from './input';
import { Loader2, MapPin } from 'lucide-react';

/**
 * AddressAutocomplete - Reusable Google Places Autocomplete Component
 * Uses the backend Google Places proxy for suggestions and place details.
 * This is the canonical address picker across signup, onboarding, settings,
 * checkout and delivery flows; it deliberately does not mix a second provider
 * into Google's ranked suggestions.
 * 
 * @param {string} value - Current address value
 * @param {function} onChange - Callback when text changes
 * @param {function} onSelect - Callback when address is selected (receives full address data)
 * @param {string} placeholder - Input placeholder text
 * @param {string} className - Additional CSS classes
 * @param {string} countryRestriction - Optional comma-separated country codes; unrestricted by default
 * @param {boolean} disabled - Whether the input is disabled
 * @param {string} data-testid - Additive passthrough applied to the underlying
 *   text input so callers can pin an input-level testid (e.g. the meal-pass
 *   wallet's "redeem-delivery-address"). Purely optional; omitted → no attr.
 */
const AddressAutocomplete = ({
  value,
  onChange,
  onSelect,
  // Checkout must not price a suggested street with the previous address parts.
  // Other callers retain the existing text-first selection behavior.
  atomicSelection = false,
  onResolvingChange,
  placeholder = "Enter address...",
  className = "",
  countryRestriction = "",
  disabled = false,
  'data-testid': dataTestId,
  id, name, required, autoComplete = 'off',
  'aria-label': ariaLabel,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchError, setSearchError] = useState('');
  const pickerRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const inputRef = useRef(null);
  const suggestionRef = useRef(null);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const resolvingCallbackRef = useRef(onResolvingChange);
  resolvingCallbackRef.current = onResolvingChange;

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (!showSuggestions) return;
    // A scrollbar/touch gesture can blur to null without leaving the picker.
    // Observe the pointer before blur, without cancelling native scrolling.
    // Outside clicks must also close a panel whose input is already blurred.
    const pointerDown = (event) => {
      pointerInsideRef.current = !!pickerRef.current?.contains(event.target);
      if (!pointerInsideRef.current) setShowSuggestions(false);
    };
    const pointerEnd = () => { pointerInsideRef.current = false; };
    document.addEventListener('pointerdown', pointerDown, true);
    document.addEventListener('pointerup', pointerEnd, true);
    document.addEventListener('pointercancel', pointerEnd, true);
    window.addEventListener('blur', pointerEnd);
    return () => {
      pointerEnd();
      document.removeEventListener('pointerdown', pointerDown, true);
      document.removeEventListener('pointerup', pointerEnd, true);
      document.removeEventListener('pointercancel', pointerEnd, true);
      window.removeEventListener('blur', pointerEnd);
    };
  }, [showSuggestions]);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    requestSequenceRef.current += 1;
    resolvingCallbackRef.current?.(false);
  }, []);

  const getSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
    return sessionTokenRef.current;
  }, []);

  // Debounced search using Google Places Autocomplete via backend
  const searchAddresses = useCallback(async (query, sequence) => {
    if (!query || query.length < 3) {
      if (sequence === requestSequenceRef.current) {
        setSuggestions([]);
        setShowSuggestions(false);
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    try {
      // Use backend endpoint for Google Places autocomplete
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/delivery/autocomplete-address`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query,
            country_restriction: countryRestriction,
            session_token: getSessionToken(),
          })
        }
      );
      
      const data = await response.json();
      
      if (sequence !== requestSequenceRef.current) return;
      if (!response.ok || !data.success) throw new Error('Address search unavailable');
      if (data.success && data.predictions && data.predictions.length > 0) {
        setSuggestions(data.predictions.map(prediction => ({
          place_id: prediction.place_id,
          description: prediction.description,
          structured_formatting: prediction.structured_formatting,
          short_name: prediction.structured_formatting?.main_text || prediction.description.split(',')[0],
          secondary_text: prediction.structured_formatting?.secondary_text || prediction.description.split(',').slice(1).join(',').trim()
        })));
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      if (sequence !== requestSequenceRef.current) return;
      setSearchError('Address suggestions are unavailable. Please enter your full address below.');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      if (sequence === requestSequenceRef.current) setLoading(false);
    }
  }, [countryRestriction, getSessionToken]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    // Invalidate an in-flight request immediately. Waiting until the next
    // debounced request starts leaves a short window where suggestions for the
    // previous prefix can overwrite what the customer is currently typing.
    const sequence = ++requestSequenceRef.current;
    resolvingCallbackRef.current?.(false);
    setSearchError('');
    setInputValue(newValue);
    onChange?.(newValue);
    setSelectedIndex(-1);
    setSuggestions([]);
    setShowSuggestions(false);

    // Debounce the search (300ms delay)
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      searchAddresses(newValue, sequence);
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion) => {
    const sequence = ++requestSequenceRef.current;
    clearTimeout(debounceRef.current);
    setSearchError('');
    setSuggestions([]);
    setShowSuggestions(false);
    setInputValue(suggestion.description);
    if (!atomicSelection) onChange?.(suggestion.description);
    resolvingCallbackRef.current?.(true);

    const selectionFailed = () => {
      if (atomicSelection) {
        setInputValue(value || '');
        setSearchError('We couldn’t fill this address. Your entries are unchanged; please complete the address manually.');
      } else {
        // Keep the full text editable, but never mark unresolved coordinates
        // as a verified selection in customer/delivery forms.
        setSearchError('We couldn’t fill this address. Please retry the suggestion or complete the address manually.');
      }
    };
    
    if (suggestion.place_id) {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/delivery/place-details`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              place_id: suggestion.place_id,
              session_token: getSessionToken(),
            })
          }
        );
        const data = await response.json();
        if (sequence !== requestSequenceRef.current) return;
        
        if (response.ok && data.success) {
          const selectedText = atomicSelection
            ? (data.street || data.formatted_address || suggestion.description)
            : (data.formatted_address || suggestion.description);
          setInputValue(selectedText);
          onChange?.(selectedText);
          onSelect?.({
            address: data.formatted_address || suggestion.description,
            formatted_address: data.formatted_address || suggestion.description,
            display_name: suggestion.description,
            latitude: data.latitude,
            longitude: data.longitude,
            place_id: suggestion.place_id,
            street: data.street,
            city: data.city,
            state: data.state,
            postal_code: data.postal_code,
            country: data.country
          });
          sessionTokenRef.current = null;
        } else {
          selectionFailed();
        }
      } catch (error) {
        if (sequence !== requestSequenceRef.current) return;
        selectionFailed();
      } finally {
        if (sequence === requestSequenceRef.current) {
          setLoading(false);
          resolvingCallbackRef.current?.(false);
        }
      }
    } else {
      selectionFailed();
      resolvingCallbackRef.current?.(false);
    }
    
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = (event) => {
    // Keep options mounted while focus moves to one of their buttons. A
    // timeout here can remove the pressed option before a slow/touch click.
    const noFocusTarget = !event.relatedTarget || event.relatedTarget === document;
    if (noFocusTarget && pointerInsideRef.current) return;
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setShowSuggestions(false);
    }
  };

  return (
    <div className="relative" ref={pickerRef} onBlur={handleBlur}>
      <div className="relative">
        <Input
          ref={inputRef}
          id={id} name={name} required={required} autoComplete={autoComplete}
          aria-label={ariaLabel}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            getSessionToken();
            if (suggestions.length > 0) setShowSuggestions(true);
          }}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={dataTestId}
          className={`pr-8 ${className}`}
        />
        {loading && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />
        )}
        {!loading && (
          <MapPin className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300" />
        )}
      </div>
      {searchError && <p role="status" className="mt-1 text-xs text-slate-600">{searchError}</p>}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionRef}
          className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              onMouseDown={event => event.preventDefault()}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-100 flex items-start gap-2 ${
                index === selectedIndex ? 'bg-slate-100' : ''
              }`}
            >
              <MapPin className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-700 truncate">{suggestion.short_name}</p>
                {suggestion.secondary_text && (
                  <p className="text-xs text-slate-500 truncate">
                    {suggestion.secondary_text}
                  </p>
                )}
              </div>
            </button>
          ))}
          <div className="px-3 py-1 text-[10px] text-slate-400 border-t">
            Powered by Google
          </div>
        </div>
      )}
    </div>
  );
};

export { AddressAutocomplete };
export default AddressAutocomplete;
